import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, RefreshCcw } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useCycleWorkItemMutations, useProjectCycles } from '@/lib/db/rxdb/hooks'
import { useTaskCycleHistory } from '@/lib/db/rxdb/hooks/entities/cycleWorkItems'
import { cn } from '@/lib/utils'

interface TaskCycleFieldProps {
  taskId: string
  projectId: string | null
  envId: string
}

/**
 * The Cycle property (design 30): which open cycle the task is planned in.
 * Membership lives in cycleworkitems, not on the task, so changing it
 * closes the old membership and adds a new one. Completed cycles keep
 * their history and aren't offered.
 */
export function TaskCycleField({ taskId, projectId, envId }: TaskCycleFieldProps) {
  const { t } = useTranslation('tasks')
  const [open, setOpen] = useState(false)
  const { cycles } = useProjectCycles(projectId ?? '')
  const { cycleHistory } = useTaskCycleHistory(taskId)
  const { createCycleWorkItem, deleteCycleWorkItem } = useCycleWorkItemMutations()

  const openCycles = useMemo(
    () =>
      (cycles ?? [])
        .filter((cycle) => !cycle.isDeleted && cycle.status !== 'completed')
        .sort((a, b) => a.startDate - b.startDate),
    [cycles],
  )
  const openIds = useMemo(() => new Set(openCycles.map((cycle) => cycle.id)), [openCycles])
  const memberships = useMemo(
    () => (cycleHistory ?? []).filter((item) => !item.isDeleted && openIds.has(item.cycleId)),
    [cycleHistory, openIds],
  )
  const current = openCycles.find((cycle) => memberships.some((m) => m.cycleId === cycle.id))
  const label = (cycle: (typeof openCycles)[number]) =>
    cycle.name ? `${t('properties.cycleNumber', { number: cycle.number })} · ${cycle.name}` : t('properties.cycleNumber', { number: cycle.number })

  const choose = async (cycleId: string | null) => {
    setOpen(false)
    if (cycleId === (current?.id ?? null)) return
    for (const membership of memberships) await deleteCycleWorkItem(membership.id)
    if (cycleId) {
      await createCycleWorkItem({
        envId,
        cycleId,
        workItemId: taskId,
        statusIdAtClose: null,
        progressTypeAtClose: null,
        addedAt: Date.now(),
        sourceCycleId: null,
      })
    }
  }

  if (!projectId || openCycles.length === 0) {
    return <span className="px-2 text-[13.5px] text-muted-foreground">{t('properties.none')}</span>
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            '-ml-2 inline-flex h-7 max-w-full items-center gap-1.5 rounded-md px-2 text-[13.5px] transition hover:bg-secondary',
            current ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          <RefreshCcw className="size-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate">{current ? label(current) : t('properties.none')}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-64 p-1">
        <button
          type="button"
          onClick={() => void choose(null)}
          className="flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-accent"
        >
          {t('properties.noCycle')}
          {!current && <Check className="ml-auto size-3.5" />}
        </button>
        {openCycles.map((cycle) => (
          <button
            key={cycle.id}
            type="button"
            onClick={() => void choose(cycle.id)}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
          >
            <span className="min-w-0 flex-1 truncate">{label(cycle)}</span>
            {cycle.status === 'active' && (
              <span className="rounded bg-brand-tint px-1.5 text-[10.5px] text-brand-tint-foreground">
                {t('properties.activeCycle')}
              </span>
            )}
            {current?.id === cycle.id && <Check className="size-3.5" />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}
